import React, { useEffect, useState } from "react";
import { API_BASE_URL } from "../../config";

// The off switch for notification emails.
//
// ONE component, mounted on both profile pages. The setting belongs to the
// ACCOUNT, not to a role -- a dual-role user has two profiles and this must
// have one value across them, so two implementations would eventually be two
// answers. Same reason the bell is one bell.
//
// It only turns off EMAIL. The in-app bell has no switch and should not get
// one: it is the only place some of this appears, and hiding a booking that is
// waiting for you is not a preference worth offering. The copy says so, rather
// than leaving somebody to wonder whether they have just made themselves
// invisible to their own bookings.
//
// Saves on change, with no Save button. This is a single boolean whose effect
// is immediate and reversible in one click -- a form around it would be
// ceremony, and a Save button somebody forgets to press is worse than no
// button at all.

export default function NotificationPreferences() {
    const [enabled, setEnabled] = useState(null);
    const [email, setEmail] = useState("");
    const [busy, setBusy] = useState(false);
    const [status, setStatus] = useState("");
    const [error, setError] = useState("");

    useEffect(() => {
        let alive = true;
        const token = localStorage.getItem("token");
        if (!token) return undefined;
        fetch(`${API_BASE_URL}/api/notifications/preferences`, {
            headers: { Authorization: `Bearer ${token}` },
        })
            .then((r) => (r.ok ? r.json() : null))
            .then((d) => {
                if (!alive || !d) return;
                setEnabled(Boolean(d.email_notifications));
                setEmail(d.email || "");
            })
            .catch(() => {});
        return () => { alive = false; };
    }, []);

    async function change(next) {
        // Optimistic, then corrected from the response. A checkbox that does
        // not move until a round trip finishes feels broken.
        setEnabled(next);
        setBusy(true);
        setStatus("");
        setError("");
        try {
            const res = await fetch(`${API_BASE_URL}/api/notifications/preferences`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${localStorage.getItem("token")}`,
                },
                body: JSON.stringify({ email_notifications: next }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                // Put it back. Leaving the box where the user moved it would
                // show a setting that is not the one stored.
                setEnabled(!next);
                setError(data.message || "Could not save that.");
                return;
            }
            setEnabled(Boolean(data.email_notifications));
            setStatus(data.email_notifications ? "Emails on." : "Emails off.");
        } catch {
            setEnabled(!next);
            setError("Network error. Nothing was changed.");
        } finally {
            setBusy(false);
        }
    }

    // Nothing at all until the current value is known: rendering an unchecked
    // box first would tell every user their emails are off for a moment.
    if (enabled === null) return null;

    return (
        <section className="np">
            <h3 className="np__title">Email notifications</h3>

            <label className="np__row">
                <input
                    type="checkbox"
                    checked={enabled}
                    disabled={busy}
                    onChange={(e) => change(e.target.checked)}
                />
                <span>
                    <span className="np__label">
                        Email me when something needs my attention
                    </span>
                    <span className="np__hint">
                        A booking waiting for your approval, or an answer to one
                        you made. At most one email at a time, to
                        {email ? ` ${email}` : " your account address"}.
                    </span>
                </span>
            </label>

            {!enabled && (
                <p className="np__off">
                    You will still see everything in the bell at the top of the
                    page — this only stops the emails.
                </p>
            )}

            {status && <p className="np__ok">{status}</p>}
            {error && <p className="np__error">{error}</p>}
        </section>
    );
}
